import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import { sanitizeInput } from "../common/security-utils.js";
import { createAccountApi, getAccountEditApi, updateAccountApi } from "./accounts-api";
import SearchWithAPI from "../common/searchbox";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import "./account-create.css";
import Back from "../../assets/svg/back.svg";

const AccountCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.id;
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [errors, setErrors] = useState({});
  const [selectedOwnerOption, setSelectedOwnerOption] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState("");
  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;
  const appUrl = process.env.REACT_APP_API_URL;

  const [formState, setFormState] = useState({
    account_name: "",
    account_owner_id: "",
    account_site: "",
    phone_number: "",
    email: "",
    fax: "",
    parent_account_id: "",
    business_type: "",
    website: "",
    annual_revenue: "",
    ownership: "",
    industry_id: "",
    employees: "",
    gst_number: "",
    pan_number: "",
    credit_days: "",
    credit_limit: "",
    discount_percentage: "",
    bill_address: "",
    bill_country_id: "",
    bill_state_id: "",
    bill_city_id: "",
    bill_zipcode: "",
    ship_address: "",
    ship_country_id: "",
    ship_state_id: "",
    ship_city_id: "",
    ship_zipcode: "",
    description: "",
    profile_image: "",
  });

  const ownershipOptions = [
    { label: "Private", value: "Private" },
    { label: "Public", value: "Public" },
    { label: "Government", value: "Government" },
    { label: "Partnership", value: "Partnership" },
  ];

  const employeeOptions = [
    { label: "1-10", value: "1" },
    { label: "11-50", value: "2" },
    { label: "51-200", value: "3" },
    { label: "201-500", value: "4" },
    { label: "500+", value: "5" },
  ];


  const businessTypes = [
    { label: "Services", value: "Services" },
    { label: "Manufacturing", value: "Manufacturing" },
    { label: "Retail", value: "Retail" },
  ];



  const [billCountryOption, setBillCountryOption] = useState(null);
  const [billStateOption, setBillStateOption] = useState(null);
  const [billCityOption, setBillCityOption] = useState(null);
  const [shipCountryOption, setShipCountryOption] = useState(null);
  const [shipStateOption, setShipStateOption] = useState(null);
  const [shipCityOption, setShipCityOption] = useState(null);
  const [industryOption, setIndustryOption] = useState(null);

  useEffect(() => {
    if (editId) {
      fetchAccountDetails();
    }
  }, [editId]);

  const fetchAccountDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getAccountEditApi(editId);
      if (response.status === 200 && response.data.success) {
        const account = response.data.account;
        const owner = account.account_owner;
        setSelectedOwnerOption(owner ? { label: owner.label, value: owner.value } : null);
        setOwnerSearch(owner?.label || "");

        setFormState({
          account_name: account.first_name || "",
          account_owner_id: owner?.value || "",
          account_site: account.account_site || "",
          phone_number: account.phone_number || "",
          email: account.email || "",
          fax: account.fax || "",
          parent_account_id: account.parent_account?.value || "",
          business_type: account.business_type || "",
          website: account.website || "",
          annual_revenue: account.annual_revenue || "",
          ownership: account.ownership || "",
          industry_id: account.industry?.value || "",
          employees: account.employees || "",
          gst_number: account.gst_number || "",
          pan_number: account.pan_number || "",
          credit_days: account.credit_days || "",
          credit_limit: account.credit_limit || "",
          discount_percentage: account.discount_percentage || "",
          bill_address: account.bill_address?.address || "",
          bill_country_id: account.bill_address?.country?.value || "",
          bill_state_id: account.bill_address?.state?.value || "",
          bill_city_id: account.bill_address?.city?.value || "",
          bill_zipcode: account.bill_address?.zipcode || "",
          ship_address: account.ship_address?.address || "",
          ship_country_id: account.ship_address?.country?.value || "",
          ship_state_id: account.ship_address?.state?.value || "",
          ship_city_id: account.ship_address?.city?.value || "",
          ship_zipcode: account.ship_address?.zipcode || "",
          description: account.description || "",
          profile_image: account.profile_image || "",
        });

        // Set options for dynamic dropdowns
        setBillCountryOption(account.bill_address?.country || null);
        setBillStateOption(account.bill_address?.state || null);
        setBillCityOption(account.bill_address?.city || null);
        setShipCountryOption(account.ship_address?.country || null);
        setShipStateOption(account.ship_address?.state || null);
        setShipCityOption(account.ship_address?.city || null);
        setIndustryOption(account.industry || null);
      }
    } catch (err) {
      console.error("Error fetching account details:", err);
      setMessage("Error loading account data.");
      setMessageType("danger");
      setShowMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: sanitizeInput(value),
    }));
    // Clear validation error when user types
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error when user selects
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setFormState((prev) => ({
        ...prev,
        profile_image: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleBillCountryDropdownChange = (option) => {
    setBillCountryOption(option);
    setBillStateOption(null);
    setBillCityOption(null);
    setFormState((prev) => ({
      ...prev,
      bill_country_id: option?.value || "",
      bill_state_id: "",
      bill_city_id: "",
    }));
    // Clear country error
    if (errors.bill_country_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.bill_country_id;
        return newErrors;
      });
    }
  };

  const handleBillStateDropdownChange = (option) => {
    setBillStateOption(option);
    setBillCityOption(null);
    setFormState((prev) => ({
      ...prev,
      bill_state_id: option?.value || "",
      bill_city_id: "",
    }));
    // Clear state error
    if (errors.bill_state_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.bill_state_id;
        return newErrors;
      });
    }
  };

  const handleBillCityDropdownChange = (option) => {
    setBillCityOption(option);
    if (option) {
      setBillStateOption({ label: option.state_name, value: option.state_id });
      setBillCountryOption({
        label: option.country_name,
        value: option.country_id,
      });
      setFormState((prev) => ({
        ...prev,
        bill_city_id: option.value,
        bill_state_id: option.state_id,
        bill_country_id: option.country_id,
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        bill_city_id: "",
      }));
    }
    // Clear city error
    if (errors.bill_city_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.bill_city_id;
        return newErrors;
      });
    }
  };

  const handleShipCountryDropdownChange = (option) => {
    setShipCountryOption(option);
    setShipStateOption(null);
    setShipCityOption(null);
    setFormState((prev) => ({
      ...prev,
      ship_country_id: option?.value || "",
      ship_state_id: "",
      ship_city_id: "",
    }));
    // Clear country error
    if (errors.ship_country_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ship_country_id;
        return newErrors;
      });
    }
  };

  const handleShipStateDropdownChange = (option) => {
    setShipStateOption(option);
    setShipCityOption(null);
    if (option) {
      setShipCountryOption({
        label: option.country_name,
        value: option.country_id,
      });
      setFormState((prev) => ({
        ...prev,
        ship_state_id: option.value,
        ship_country_id: option.country_id,
        ship_city_id: "",
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        ship_state_id: "",
      }));
    }
    // Clear state error
    if (errors.ship_state_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ship_state_id;
        return newErrors;
      });
    }
  };

  const handleShipCityDropdownChange = (option) => {
    setShipCityOption(option);
    if (option) {
      setShipStateOption({ label: option.state_name, value: option.state_id });
      setShipCountryOption({
        label: option.country_name,
        value: option.country_id,
      });
      setFormState((prev) => ({
        ...prev,
        ship_city_id: option.value,
        ship_state_id: option.state_id,
        ship_country_id: option.country_id,
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        ship_city_id: "",
      }));
    }
    // Clear city error
    if (errors.ship_city_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ship_city_id;
        return newErrors;
      });
    }
  };

  const handleIndustryDropdownChange = (option) => {
    setIndustryOption(option);
    setFormState((prev) => ({
      ...prev,
      industry_id: option?.value || "",
    }));
    // Clear industry error
    if (errors.industry_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.industry_id;
        return newErrors;
      });
    }
  };

  const isValidUrl = (value) => {
    if (!value || typeof value !== "string") return false;
    const trimmedValue = value.trim();
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
    return urlPattern.test(trimmedValue);
  };

  const isValidEmail = (value) => {
    if (!value || typeof value !== "string") return false;
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value.trim());
  };

  const isValidGST = (value) => {
    if (!value || typeof value !== "string") return false;
    // Standard GSTIN: 2-digit state code, 10-char PAN, 1 entity char,
    // fixed "Z", then an alphanumeric checksum (letter or digit).
    const gstPattern =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstPattern.test(value.trim().toUpperCase());
  };

  const handleSubmit = async () => {
    const validationErrors = {};

    // Required field: Account Name
    if (!formState.account_name?.trim()) {
      validationErrors.account_name = "Account Name is required.";
    }

    // Required field: Account Owner
    if (!formState.account_owner_id) {
      validationErrors.account_owner_id = "Account Owner is required.";
    }
    // Required field: Account Site
    if (!formState.account_site?.trim()) {
      validationErrors.account_site = "Account Site is required.";
    }

    // Required field: Business Type
    if (!formState.business_type?.trim()) {
      validationErrors.business_type = "Business Type is required.";
    }

    // Required field: Industry
    if (!formState.industry_id) {
      validationErrors.industry_id = "Industry is required.";
    }

    // Required field: GST Number
    if (!formState.gst_number?.trim()) {
      validationErrors.gst_number = "GST Number is required.";
    } else if (formState.gst_number && !isValidGST(formState.gst_number)) {
      validationErrors.gst_number = "Enter a valid GST number.";
    }

    // Optional field validation: Phone (if filled: 7–15 digits, optional +)
    const cleanPhone = (formState.phone_number || "").replace(/[\s\-()]/g, "");
    if (cleanPhone && !/^\+?[0-9]{7,15}$/.test(cleanPhone)) {
      validationErrors.phone_number = "Enter a valid phone number (7–15 digits, optional + country code).";
    }

    // Optional field validation: Email (if filled, must be valid)
    if (formState.email?.trim() && !isValidEmail(formState.email)) {
      validationErrors.email = "Enter a valid email address.";
    }

    // Optional field validation: Discount % (if filled, must be 0-100)
    if (
      formState.discount_percentage !== "" &&
      (Number(formState.discount_percentage) < 0 || Number(formState.discount_percentage) > 100)
    ) {
      validationErrors.discount_percentage = "Enter a discount between 0 and 100.";
    }

    // Required field: Credit Days
    if (!formState.credit_days) {
      validationErrors.credit_days = "Payment due days is required.";
    }

    // Required field: Billing Address
    if (!formState.bill_address?.trim()) {
      validationErrors.bill_address = "Billing Address is required.";
    }

    // Required field: Billing Country
    if (!formState.bill_country_id) {
      validationErrors.bill_country_id = "Billing Country is required.";
    }

    // Required field: Billing State
    if (!formState.bill_state_id) {
      validationErrors.bill_state_id = "Billing State is required.";
    }

    // Required field: Billing City
    if (!formState.bill_city_id) {
      validationErrors.bill_city_id = "Billing City is required.";
    }

    // Required field: Billing Zip Code
    if (!formState.bill_zipcode?.trim()) {
      validationErrors.bill_zipcode = "Billing Zip Code is required.";
    }

    // Required field: Shipping Address
    if (!formState.ship_address?.trim()) {
      validationErrors.ship_address = "Shipping Address is required.";
    }

    // Required field: Shipping Country
    if (!formState.ship_country_id) {
      validationErrors.ship_country_id = "Shipping Country is required.";
    }

    // Required field: Shipping State
    if (!formState.ship_state_id) {
      validationErrors.ship_state_id = "Shipping State is required.";
    }

    // Required field: Shipping City
    if (!formState.ship_city_id) {
      validationErrors.ship_city_id = "Shipping City is required.";
    }

    // Required field: Shipping Zip Code
    if (!formState.ship_zipcode?.trim()) {
      validationErrors.ship_zipcode = "Shipping Zip Code is required.";
    }

    // Optional field validation: Website (if filled, must be valid)
    if (formState.website?.trim() && !isValidUrl(formState.website)) {
      validationErrors.website = "Enter a valid website URL.";
    }

    // If there are validation errors, show them all and return
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setMessage("Please fill in all required fields.");
      setMessageType("danger");
      setShowMessage(true);
      return;
    }

    setIsSaving(true);
    setShowMessage(false);
    setErrors({});

    const payload = {
      ...formState,
      phone_number: cleanPhone,
      account_name: sanitizeInput(formState.account_name),
      account_owner_id: formState.account_owner_id ? sanitizeInput(String(formState.account_owner_id)) : null,
      parent_account_id: formState.parent_account_id ? sanitizeInput(String(formState.parent_account_id)) : null,
      industry_id: formState.industry_id ? sanitizeInput(String(formState.industry_id)) : null,
      employees: formState.employees ? Number(formState.employees) : null,

      bill_address: sanitizeInput(formState.bill_address),
      bill_country_id: formState.bill_country_id ? sanitizeInput(String(formState.bill_country_id)) : null,
      bill_state_id: formState.bill_state_id ? sanitizeInput(String(formState.bill_state_id)) : null,
      bill_city_id: formState.bill_city_id ? sanitizeInput(String(formState.bill_city_id)) : null,
      bill_zipcode: sanitizeInput(formState.bill_zipcode),

      ship_address: sanitizeInput(formState.ship_address),
      ship_country_id: formState.ship_country_id ? sanitizeInput(String(formState.ship_country_id)) : null,
      ship_state_id: formState.ship_state_id ? sanitizeInput(String(formState.ship_state_id)) : null,
      ship_city_id: formState.ship_city_id ? sanitizeInput(String(formState.ship_city_id)) : null,
      ship_zipcode: sanitizeInput(formState.ship_zipcode),

      annual_revenue: formState.annual_revenue ? Number(formState.annual_revenue) : null,
      credit_limit: formState.credit_limit ? Number(formState.credit_limit) : null,
      credit_days: formState.credit_days ? Number(formState.credit_days) : null,
      discount_percentage: formState.discount_percentage ? Number(formState.discount_percentage) : null,
    };

    try {
      let response;
      if (editId) {
        response = await updateAccountApi(editId, payload);
      } else {
        response = await createAccountApi(payload);
      }
      // console.log("PAYLOAD:", payload);
      // console.log("SAVE RESPONSE:", response);
      // console.log("SAVE ERROR:", response?.data?.error);
      if (response.status === 200 && response.data.success === true) {
        setMessage(response.data.message || "Account saved successfully");
        setMessageType("success");
        setShowMessage(true);
        navigate(-1);
        return;
      }

      const errorMessage = response.data?.message || "Unable to save account.";
      setMessage(errorMessage);
      setMessageType("danger");
      setShowMessage(true);

      if (response.data?.error) {
        const validationErrors = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          validationErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors(validationErrors);
      }
    } catch (err) {
      setMessage("Server error while saving account.");
      setMessageType("danger");
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="account-page d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <Loader height="80vh" />
      </div>
    );
  }

  return (
    <div className="account-container">
      <div className="account-header">
        <div className="account-header-left">
          <div className="account-back-btn" onClick={() => navigate(-1)}>
            <img src={Back} alt="Back" />
          </div>
          <div className="account-page-title">
            {editId ? "Edit Account" : "New Account"}
          </div>
        </div>

        <div className="account-header-actions">
          <button className="btn-cancel" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? (
              <Spinner animation="border" size="sm" style={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>

      <div className="account-card">
        <div className="account-section-title">Account information</div>

        <div className="row g-3">
          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">
                Account Name<span className="required-star">*</span>
              </div>
              <input
                type="text"
                name="account_name"
                value={formState.account_name}
                onChange={handleInputChange}
                placeholder="Enter name here..."
                className="form-input"
              />
              {errors.account_name && (
                <span className="error-text">{errors.account_name}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">
                Account Owner<span className="required-star">*</span>
              </div>
              <SearchWithFreeTextAPI
                apiUrl={apiAccUrl + "get_employee"}
                placeholder="Select owner"
                selectedOption={selectedOwnerOption}
                selectedValue={ownerSearch}
                onChangeSearch={(term) => setOwnerSearch(term)}
                onDropdownChange={(option) => {
                  setSelectedOwnerOption(option);
                  setOwnerSearch(option?.label || "");
                  setFormState((prev) => ({
                    ...prev,
                    account_owner_id: option?.value || "",
                  }));
                  // Clear owner error
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.account_owner_id;
                    return newErrors;
                  });
                }}
              />
              {errors.account_owner_id && (
                <span className="error-text">{errors.account_owner_id}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">
                Account Site<span className="required-star">*</span>
              </div>
              <input
                type="text"
                name="account_site"
                value={formState.account_site}
                onChange={handleInputChange}
                placeholder="Enter here..."
                className="form-input"
              />
              {errors.account_site && (
                <span className="error-text">{errors.account_site}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Phone Number</div>
              <input
                type="text"
                name="phone_number"
                value={formState.phone_number}
                onChange={handleInputChange}
                placeholder="+91 Enter here..."
                className="form-input"
              />
              {errors.phone_number && (
                <span className="error-text">{errors.phone_number}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Email</div>
              <input
                type="email"
                name="email"
                value={formState.email}
                onChange={handleInputChange}
                placeholder="Enter here..."
                className="form-input"
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Fax</div>
              <input
                type="text"
                name="fax"
                value={formState.fax}
                onChange={handleInputChange}
                placeholder="Enter here..."
                className="form-input"
              />
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Parent Account</div>
              <input
                type="text"
                name="parent_account_id"
                value={formState.parent_account_id}
                onChange={handleInputChange}
                placeholder="Enter account ID..."
                className="form-input"
              />
              {errors.parent_account_id && (
                <span className="error-text">{errors.parent_account_id}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">
                Business Type<span className="required-star">*</span>
              </div>
              <select
                name="business_type"
                value={formState.business_type}
                onChange={handleSelectChange}
                className="form-select"
              >
                <option value="">Select</option>
                {businessTypes.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.business_type && (
                <span className="error-text">{errors.business_type}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Website</div>
              <input
                type="text"
                name="website"
                value={formState.website}
                onChange={handleInputChange}
                placeholder="Enter here..."
                className="form-input"
              />
              {errors.website && (
                <span className="error-text">{errors.website}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Annual Revenue</div>
              <input
                type="text"
                name="annual_revenue"
                value={formState.annual_revenue}
                onChange={handleInputChange}
                placeholder="Enter here..."
                className="form-input"
              />
              {errors.annual_revenue && (
                <span className="error-text">{errors.annual_revenue}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">Ownership</div>
              <select
                name="ownership"
                value={formState.ownership}
                onChange={handleSelectChange}
                className="form-select"
              >
                <option value="">Select</option>
                {ownershipOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.ownership && (
                <span className="error-text">{errors.ownership}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">
                Industry<span className="required-star">*</span>
              </div>
              <SearchWithAPI
                apiUrl={appUrl + "industry_type"}
                onDropdownChange={handleIndustryDropdownChange}
                placeholder="Select Industry"
                selectedOption={industryOption}
              />
              {errors.industry_id && (
                <span className="error-text">{errors.industry_id}</span>
              )}
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="form-group">
              <div className="form-label">No. of Employees</div>
              <select
                name="employees"
                value={formState.employees}
                onChange={handleSelectChange}
                className="form-select"
              >
                <option value="">Select</option>
                {employeeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.employees && (
                <span className="error-text">{errors.employees}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-6">
          <div className="account-card h-100 mb-0">
            <div className="account-card-title">Tax/Business Info</div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">
                    GST Number<span className="required-star">*</span>
                  </div>
                  <input
                    type="text"
                    name="gst_number"
                    value={formState.gst_number}
                    onChange={handleInputChange}
                    placeholder="Enter here..."
                    className="form-input"
                  />
                  {errors.gst_number && (
                    <span className="error-text">{errors.gst_number}</span>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">PAN Number</div>
                  <input
                    type="text"
                    name="pan_number"
                    value={formState.pan_number}
                    onChange={handleInputChange}
                    placeholder="Enter here..."
                    className="form-input"
                  />
                  {errors.pan_number && (
                    <span className="error-text">{errors.pan_number}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="account-card h-100 mb-0">
            <div className="account-card-title">Payment</div>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">
                    Payment due days
                  </div>
                  <input
                    type="number"
                    name="credit_days"
                    value={formState.credit_days}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="form-input"
                  />
                  {errors.credit_days && (
                    <span className="error-text">{errors.credit_days}</span>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Credit limit</div>
                  <input
                    type="text"
                    name="credit_limit"
                    value={formState.credit_limit}
                    onChange={handleInputChange}
                    placeholder="Enter here..."
                    className="form-input"
                  />
                  {errors.credit_limit && (
                    <span className="error-text">{errors.credit_limit}</span>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <div className="form-label">Discount %</div>
                  <input
                    type="number"
                    name="discount_percentage"
                    value={formState.discount_percentage}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    className="form-input"
                  />
                  {errors.discount_percentage && (
                    <span className="error-text">{errors.discount_percentage}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Address Cards Row */}
      <div className="row g-4 mt-1">
        {/* Billing Address Card */}
        <div className="col-12 col-lg-6">
          <div className="account-card h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="account-card-title mb-0">
                Billing Address<span className="required-star">*</span>
              </div>
            </div>
            <div className="d-flex flex-column gap-3">
              <div className="form-group">
                <div className="form-label">
                  Address<span className="required-star">*</span>
                </div>
                <input
                  type="text"
                  name="bill_address"
                  value={formState.bill_address}
                  onChange={handleInputChange}
                  placeholder="Enter here..."
                  className="form-input"
                />
                {errors.bill_address && (
                  <span className="error-text">{errors.bill_address}</span>
                )}
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      Country<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleBillCountryDropdownChange}
                      apiUrl={apiAccUrl + "get_country"}
                      params={{}}
                      placeholder={"Select"}
                      selectedOption={billCountryOption}
                    />
                    {errors.bill_country_id && (
                      <span className="error-text">{errors.bill_country_id}</span>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      State<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleBillStateDropdownChange}
                      apiUrl={apiAccUrl + "get_state"}
                      params={{
                        country_id: billCountryOption?.value || "",
                      }}
                      placeholder={"Select"}
                      selectedOption={billStateOption}
                    />
                    {errors.bill_state_id && (
                      <span className="error-text">{errors.bill_state_id}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      City<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleBillCityDropdownChange}
                      apiUrl={apiAccUrl + "get_city"}
                      params={{
                        state_id: billStateOption?.value || "",
                      }}
                      placeholder={"Select"}
                      selectedOption={billCityOption}
                    />
                    {errors.bill_city_id && (
                      <span className="error-text">{errors.bill_city_id}</span>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      Zip code<span className="required-star">*</span>
                    </div>
                    <input
                      type="text"
                      name="bill_zipcode"
                      value={formState.bill_zipcode}
                      onChange={handleInputChange}
                      placeholder="Enter here..."
                      className="form-input"
                    />
                    {errors.bill_zipcode && (
                      <span className="error-text">{errors.bill_zipcode}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Address Card */}
        <div className="col-12 col-lg-6">
          <div className="account-card h-100">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="account-card-title mb-0">
                Shipping Address<span className="required-star">*</span>
              </div>
              <button
                type="button"
                className="copy-address-btn"
                onClick={() => {
                  setFormState((prev) => ({
                    ...prev,
                    ship_address: prev.bill_address,
                    ship_country_id: prev.bill_country_id,
                    ship_state_id: prev.bill_state_id,
                    ship_city_id: prev.bill_city_id,
                    ship_zipcode: prev.bill_zipcode,
                  }));
                  setShipCountryOption(billCountryOption);
                  setShipStateOption(billStateOption);
                  setShipCityOption(billCityOption);
                }}
              >
                Copy billing address
              </button>
            </div>
            <div className="d-flex flex-column gap-3">
              <div className="form-group">
                <div className="form-label">
                  Address<span className="required-star">*</span>
                </div>
                <input
                  type="text"
                  name="ship_address"
                  value={formState.ship_address}
                  onChange={handleInputChange}
                  placeholder="Enter here..."
                  className="form-input"
                />
                {errors.ship_address && (
                  <span className="error-text">{errors.ship_address}</span>
                )}
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      Country<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleShipCountryDropdownChange}
                      apiUrl={apiAccUrl + "get_country"}
                      params={{}}
                      placeholder={"Select"}
                      selectedOption={shipCountryOption}
                    />
                    {errors.ship_country_id && (
                      <span className="error-text">{errors.ship_country_id}</span>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      State<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleShipStateDropdownChange}
                      apiUrl={apiAccUrl + "get_state"}
                      params={{
                        country_id: shipCountryOption?.value || "",
                      }}
                      placeholder={"Select"}
                      selectedOption={shipStateOption}
                    />
                    {errors.ship_state_id && (
                      <span className="error-text">{errors.ship_state_id}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      City<span className="required-star">*</span>
                    </div>
                    <SearchWithAPI
                      onDropdownChange={handleShipCityDropdownChange}
                      apiUrl={apiAccUrl + "get_city"}
                      params={{
                        state_id: shipStateOption?.value || "",
                      }}
                      placeholder={"Select"}
                      selectedOption={shipCityOption}
                    />
                    {errors.ship_city_id && (
                      <span className="error-text">{errors.ship_city_id}</span>
                    )}
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <div className="form-label">
                      Zip code<span className="required-star">*</span>
                    </div>
                    <input
                      type="text"
                      name="ship_zipcode"
                      value={formState.ship_zipcode}
                      onChange={handleInputChange}
                      placeholder="Enter here..."
                      className="form-input"
                    />
                    {errors.ship_zipcode && (
                      <span className="error-text">{errors.ship_zipcode}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="account-card mt-4">
        <div className="account-section-title mb-4">Description</div>
        <textarea
          name="description"
          value={formState.description}
          onChange={handleInputChange}
          placeholder="Enter here..."
          className="form-textarea"
        />
        {errors.description && (
          <span className="error-text">{errors.description}</span>
        )}
      </div>
    </div>
  );
};

export default AccountCreate;
